import * as React from 'react';
import { FC, memo, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { withAddIncentiveToCart } from 'components/with-add-incentive-to-cart';
import { RedemptionMethod } from 'components/with-add-incentive-to-cart/types';
import { useFavoriteOffers } from 'hooks/favorite-offers';
import { useGetUrlSearchParam } from 'hooks/location/use-get-url-search-param';
import { useEnableGreyedOutOffers } from 'hooks/use-enable-greyed-out-offers';
import { isGreyedOutOffer } from 'pages/loyalty/loyalty-offers/utils';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useFeaturesEarningCalculations } from 'state/loyalty/hooks/use-feature-earning-calculations';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { LoyaltyOffer, isLoyaltyOffer, isReward, isValidOfferIncentive } from 'state/loyalty/types';
import { isUpcomingOffer } from 'state/loyalty/utils';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { getFirstStringInLocaleBlockContent } from 'utils/sanity';

import { parseIncentiveData } from '../incentive-card/parse-incentive-data';
import { InvalidServiceModeModal } from '../invalid-service-mode-modal';
import { IncentiveDetailsWithAnimation } from '../loyalty-incentives.styled';
import { useLoyaltyIncentivesAvailability } from '../use-loyalty-incentives-availability';

import { ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA } from './constants';
import { IncentiveDetailsView } from './incentive-details.view';
import { IIncentiveDetailsProps, LoyaltyRedemptionTypes } from './types';

export const MOBILE_REDEMPTION: string[] = [
  LoyaltyRedemptionTypes.MOBILE_ONLY,
  LoyaltyRedemptionTypes.MOBILEANDRESTAURANT,
];
export const RESTAURANT_REDEMPTION: string[] = [
  LoyaltyRedemptionTypes.RESTAURANT_ONLY,
  LoyaltyRedemptionTypes.MOBILEANDRESTAURANT,
];

/**
 *
 * IncentiveDetails displays an expanded view of a incentive when selected from the incentive list
 *
 */
export const IncentiveDetails: FC<IIncentiveDetailsProps> = withAddIncentiveToCart(
  ({
    selectedIncentive,
    engineIncentivesMap,
    addIncentiveToCart,
    challengeIncentiveSelected,
    isWidget = false,
  }) => {
    const incentiveLoyaltyEngineId = selectedIncentive?.loyaltyEngineId || '';
    const engineReward = engineIncentivesMap[incentiveLoyaltyEngineId];

    const {
      pointCost,
      locked,
      redemptionPercentage,
      remainingPointsNeededForRedemption,
      rewardBenefitId,
      requiredLoyaltyTier,
    } = engineReward || {};

    const cdp = useCdpContext();

    const { removeFromCart } = useOrderContext();
    const { clearInRestaurantRedemptionAllRewards } = useInRestaurantRedemptionContext();
    const rewardData = parseIncentiveData(selectedIncentive);
    const { loyaltyUser } = useLoyaltyUser();
    const { isRewardTierLocked } = useLoyaltyRewards(loyaltyUser);
    const { loyaltyTierKey: userTier, pointsEarned } = useLoyaltyUserTier();
    const { getTierEarningData } = useFeaturesEarningCalculations();
    const { noStoreSelected } = useStoreContext();
    const { isAuthenticated } = useAuthContext();
    const { enableGreyedOutOffers } = useEnableGreyedOutOffers(isAuthenticated());

    const location = useLocation();
    const { formatMessage } = useIntl();
    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const [showRemoveRewardButton, setShowRemoveRewardButton] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const enableLoyaltyInApps = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_IN_APPS);
    const enableMobileOffer = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_MOBILE);
    const enableSwitchServiceModeModal = useFlag(LaunchDarklyFlag.ENABLE_SWITCH_SERVICE_MODE_MODAL);

    const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);

    const tierLocked = isRewardTierLocked(engineReward, userTier);
    const nextTierPoints = getTierEarningData(requiredLoyaltyTier)?.pointsNeeded || 0;
    const tierPointsNeeded = nextTierPoints - pointsEarned;
    const tierPointsPercentage = nextTierPoints ? (pointsEarned / nextTierPoints) * 100 : 0;

    const { incentiveId, incentiveType } =
      useMemo(() => {
        const incentive = isReward(selectedIncentive)
          ? selectedIncentive.incentives?.find(benefit => benefit?._id === rewardBenefitId)
          : selectedIncentive.incentives?.[0];
        return {
          incentiveId: isValidOfferIncentive(incentive) ? incentive?._id : '',
          incentiveType: incentive?._type,
        };
      }, [rewardBenefitId, selectedIncentive]) || {};
    const dispatch = useAppDispatch();
    const appliedLoyaltyRewardsArray = useAppSelector(
      selectors.loyalty.selectAppliedLoyaltyRewardsArray
    );
    const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
    const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);

    const { checkIncentiveAvailability, incentiveUnavailableMessage } =
      useLoyaltyIncentivesAvailability(selectedIncentive, pointCost, locked);
    const exceedsBalanceError = formatMessage({ id: 'exceedsRewardsBalance' });

    const { toggleFavorite, isFavorite, isOfferLoading } = useFavoriteOffers();

    // if the redemptionMethod is not defined set mobileAndRestaurant as default method
    const redemptionMethod =
      selectedIncentive?.redemptionMethod || LoyaltyRedemptionTypes.MOBILEANDRESTAURANT;

    useEffect(() => {
      if (incentiveUnavailableMessage) {
        setErrorMessage(incentiveUnavailableMessage);
        setShowRemoveRewardButton(incentiveUnavailableMessage === exceedsBalanceError);
        return;
      }
      if (noStoreSelected) {
        setErrorMessage('');
        return;
      }

      // Check incentive item availability in selected store
      (async () => {
        setErrorMessage('');
        setCheckingAvailability(true);
        try {
          const isAvailable =
            incentiveType !== 'offerDiscount'
              ? await checkIncentiveAvailability(`${incentiveType}-${incentiveId}`)
              : true;
          const storeError = isAvailable ? '' : formatMessage({ id: 'nonAvailableItemInStore' });
          setErrorMessage(storeError);
          setCheckingAvailability(false);
        } catch {
          setCheckingAvailability(false);
        }
      })();
    }, [
      incentiveId,
      incentiveType,
      checkIncentiveAvailability,
      exceedsBalanceError,
      formatMessage,
      noStoreSelected,
      incentiveUnavailableMessage,
    ]);

    const incentiveEvaluationResults = offersFeedbackMap[incentiveLoyaltyEngineId];
    const handleRemoveRewardsClick = () => {
      if (appliedLoyaltyRewardsArray?.length) {
        appliedLoyaltyRewardsArray.forEach(appliedReward => {
          removeFromCart(appliedReward);
          dispatch(
            actions.loyalty.removeAppliedReward({
              cartId: appliedReward.cartId,
              rewardBenefitId: appliedReward.rewardId,
            })
          );
        });
        clearInRestaurantRedemptionAllRewards();
        removeAppliedRewardsInStorage();

        setShowRemoveRewardButton(false);
      }
    };

    const showRedeemRewardInRestaurantCTAValue = useGetUrlSearchParam(
      ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA
    );
    const onlyShowRedeemRewardInRestaurantCTA = showRedeemRewardInRestaurantCTAValue === 'true';

    const isLoyaltyOrReward =
      isLoyaltyOffer(selectedIncentive) || (isReward(selectedIncentive) && enableLoyaltyInApps);

    const loyaltyInRestaurantRedemptionAvailable = RESTAURANT_REDEMPTION.includes(redemptionMethod);

    // only when Restaurant Redemption set in Sanity
    const canAddToInRestaurantOrder = isLoyaltyOrReward && loyaltyInRestaurantRedemptionAvailable;

    const loyaltyMobileRedemptionAvailable =
      enableMobileOffer && MOBILE_REDEMPTION.includes(redemptionMethod);

    const canAddToMobileOrder =
      !onlyShowRedeemRewardInRestaurantCTA && loyaltyMobileRedemptionAvailable && isLoyaltyOrReward;

    const offerIsClosed =
      enableGreyedOutOffers &&
      rewardData.isOffer &&
      isGreyedOutOffer(selectedIncentive as unknown as LoyaltyOffer, isAuthenticated());

    const offerIsUpcoming = isUpcomingOffer(selectedIncentive);

    return (
      <IncentiveDetailsWithAnimation>
        {enableSwitchServiceModeModal &&
          !checkingAvailability &&
          incentiveEvaluationResults?.length && (
            <InvalidServiceModeModal incentiveEvaluationResults={incentiveEvaluationResults} />
          )}
        <IncentiveDetailsView
          {...rewardData}
          pointCost={pointCost}
          locked={locked}
          closedOffer={offerIsClosed}
          tierLocked={tierLocked}
          requiredLoyaltyTier={requiredLoyaltyTier}
          tierPointsNeeded={tierPointsNeeded}
          redemptionPercentage={tierLocked ? tierPointsPercentage : redemptionPercentage}
          handleRedeemRewardClick={() => {
            cdp.trackEvent({
              name: CustomEventNames.BUTTON_CLICK,
              type: EventTypes.Other,
              attributes: {
                Name: 'Add to Mobile Order',
                Path: location.pathname,
              },
            });
            addIncentiveToCart({ incentive: selectedIncentive, method: RedemptionMethod.MOBILE });
          }}
          handleInRestaurantRedemptionClick={() => {
            cdp.trackEvent({
              name: CustomEventNames.BUTTON_CLICK,
              type: EventTypes.Other,
              attributes: {
                Name: 'Reedem In Restaurant',
                Path: location.pathname,
              },
            });

            if (isLoyaltyOffer(selectedIncentive)) {
              dispatch(actions.loyalty.setSelectedOffer(selectedIncentive));
            }
            addIncentiveToCart({
              incentive: selectedIncentive,
              method: RedemptionMethod.IN_RESTAURANT,
            });
            cdp.trackEvent({
              name: isLoyaltyOffer(selectedIncentive)
                ? CustomEventNames.OFFER_ADDED_TO_ORDER
                : CustomEventNames.REWARD_ADDED_TO_ORDER,
              type: EventTypes.Other,
              attributes: {
                engineId: selectedIncentive.loyaltyEngineId ?? '',
                sanityId: selectedIncentive._id,
                name: isReward(selectedIncentive)
                  ? (selectedIncentive.name?.locale ?? '')
                  : getFirstStringInLocaleBlockContent(selectedIncentive.name),
                redemptionMode: 'In-Store',
              },
            });
          }}
          remainingPointsNeededForRedemption={remainingPointsNeededForRedemption}
          errorMessage={errorMessage}
          key={rewardData.id}
          checkingAvailability={checkingAvailability}
          canAddToInRestaurantOrder={canAddToInRestaurantOrder}
          canAddToMobileOrder={canAddToMobileOrder}
          showRemoveRewardButton={isWidget && showRemoveRewardButton}
          handleRemoveRewardsClick={handleRemoveRewardsClick}
          incentiveEvaluationResults={incentiveEvaluationResults}
          offersInCooldown={loyaltyOffersInCooldown}
          favorited={enableFavoriteOffers && isFavorite(selectedIncentive._id || '')}
          favoriteLoading={isOfferLoading(selectedIncentive._id || '')}
          handleToggleFavorite={toggleFavorite}
          challengeIncentiveSelected={challengeIncentiveSelected}
          isUpcomingOffer={offerIsUpcoming}
          isWidget={isWidget}
        />
      </IncentiveDetailsWithAnimation>
    );
  }
);

export const IncentiveDetailsMemo = memo(withAddIncentiveToCart(IncentiveDetails));
